import React, { useEffect, useMemo, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Pagination,
  Typography,
  Skeleton,
} from "@mui/material";
import TopNavbar from "../../components/TopNavBar/TopNavBar";
import ChatInterface from "../../components/ChatInterface/ChatInterface";
import ProductCard from "../../components/ProductCard/ProductCard";
import ProductInfo from "../../components/ProductInfo/ProductInfo";
import {
  PageContainer,
  ProductCardGrid,
  MainContent,
  SidebarContainer,
} from "./ProductLandingPage.styles";
import { getProducts, getProductStats, getProductById } from "../../api/api";

const PAGE_SIZE = 20;

export default function ProductLandingPage() {
  const location = useLocation();
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [chatFilters, setChatFilters] = useState(null);
  const [chatQuery, setChatQuery] = useState("");
  const [chatProducts, setChatProducts] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [usingChatProducts, setUsingChatProducts] = useState(false);
  // True when the chat ran a product search that returned zero matches. In that
  // case the grid must show an empty "no results" state instead of silently
  // reverting to the full default catalog.
  const [chatNoResults, setChatNoResults] = useState(false);
  const isManualFilterChange = useRef(false);
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productInfoOpen, setProductInfoOpen] = useState(false);

  const getInitialFilters = () => {
    const params = new URLSearchParams(location.search);
    return {
      search: params.get("q") || params.get("search") || "",
      brand: params.get("brand") || "",
      category: params.get("category") || "",
      condition: params.get("condition") || "",
      priceRange: [0, 1000],
      stock: "",
    };
  };

  const [filters, setFilters] = useState(getInitialFilters());

  // Check for productId in URL and open ProductInfo modal
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const productId = params.get("productId");
    
    if (productId) {
      const fetchAndOpenProduct = async () => {
        try {
          const product = await getProductById(productId);
          if (product) {
            setSelectedProduct(product);
            setProductInfoOpen(true);
            // Remove productId from URL without reload
            const newParams = new URLSearchParams(location.search);
            newParams.delete("productId");
            const newUrl = `${window.location.pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`;
            window.history.replaceState({}, '', newUrl);
          }
        } catch (error) {
          console.error("Failed to fetch product:", error);
        }
      };
      fetchAndOpenProduct();
    }
  }, [location.search]);

  useEffect(() => {
    const onChatFilters = (e) => {
      const detail = e.detail || {};
      const f = detail.filters || {};
      const products = detail.products || [];

      setChatFilters(f);
      setChatQuery(detail.query || "");
      setSessionId(detail.sessionId || "");
      setChatProducts(products);

      const newFilters = {
        search: f.search || "",
        brand: f.brand || "",
        category: f.category || "",
        condition: f.condition || "",
        priceRange: [
          typeof f.price_min === "number" ? f.price_min : 0,
          typeof f.price_max === "number" ? f.price_max : 1000,
        ],
        stock:
          f.in_stock === true
            ? "in_stock"
            : f.in_stock === false
            ? "out_of_stock"
            : "",
      };
      setFilters(newFilters);
    };
    window.addEventListener("chat:filters", onChatFilters);
    return () => window.removeEventListener("chat:filters", onChatFilters);
  }, []);

  useEffect(() => {
    const onMCPProducts = (e) => {
      const detail = e.detail || {};
      const f = detail.filters || {};
      const products = Array.isArray(detail.products) ? detail.products : [];

      const hadQuery = !!(detail.query && String(detail.query).trim());

      setChatFilters(f);
      setChatQuery(detail.query || "");
      setSessionId(detail.sessionId || "");
      setChatProducts(products);

      if (products.length > 0) {
        setUsingChatProducts(true);
        setChatNoResults(false);
        isManualFilterChange.current = false;
      } else if (hadQuery) {
        // Chat searched but found nothing: show an empty "no results" grid
        // scoped to the query rather than reverting to the full catalog.
        setUsingChatProducts(true);
        setChatNoResults(true);
        setProducts([]);
        isManualFilterChange.current = false;
      } else {
        setUsingChatProducts(false);
        setChatNoResults(false);
      }

      const newFilters = {
        ...getInitialFilters(),
        search: f.search_text || "",
        brand: f.brand || "",
        category: f.category || "",
        minPrice: f.min_price || "",
        maxPrice: f.max_price || "",
        condition: f.condition || "",
        stockOnly: f.stock_only === true ? "in_stock" : "",
      };
      setFilters(newFilters);
    };

    window.addEventListener("mcp:products", onMCPProducts);
    return () => window.removeEventListener("mcp:products", onMCPProducts);
  }, []);

  // Listen for chat clear event to reset products and filters
  useEffect(() => {
    const onChatClear = (e) => {
      // Reset all chat-related state
      setChatProducts([]);
      setUsingChatProducts(false);
      setChatNoResults(false);
      setChatFilters(null);
      setChatQuery("");
      setSessionId(e.detail?.sessionId || "");
      
      // Reset filters to initial state (empty filters to show all products)
      const initialFilters = {
        search: "",
        brand: "",
        category: "",
        condition: "",
        priceRange: [0, 1000],
        stock: "",
      };
      setFilters(initialFilters);
      
      // Reset pagination to first page
      setPage(1);
      
      // Mark as manual filter change to prevent auto-updates
      isManualFilterChange.current = true;
    };

    window.addEventListener("chat:clear", onChatClear);
    return () => window.removeEventListener("chat:clear", onChatClear);
  }, []);

  const apiQueryString = useMemo(() => {
    const p = new URLSearchParams();
    const skip = (page - 1) * PAGE_SIZE;
    p.set("skip", String(skip));
    p.set("limit", String(PAGE_SIZE));

    if (filters.search) p.set("search", filters.search);
    if (filters.brand) p.set("brand", filters.brand);
    if (filters.condition) p.set("condition", filters.condition);
    if (filters.category) p.set("category", filters.category);
    if (filters.stock === "in_stock") p.set("in_stock", "true");
    if (filters.stock === "out_of_stock") p.set("in_stock", "false");

    if (Array.isArray(filters.priceRange) && filters.priceRange.length === 2) {
      p.set("price_min", String(filters.priceRange[0]));
      p.set("price_max", String(filters.priceRange[1]));
    }
    return p.toString();
  }, [page, filters]);

  const statsQueryString = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.search) p.set("search", filters.search);
    if (filters.brand) p.set("brand", filters.brand);
    if (filters.condition) p.set("condition", filters.condition);
    if (filters.category) p.set("category", filters.category);
    if (filters.stock === "in_stock") p.set("in_stock", "true");
    if (filters.stock === "out_of_stock") p.set("in_stock", "false");

    if (Array.isArray(filters.priceRange) && filters.priceRange.length === 2) {
      p.set("price_min", String(filters.priceRange[0]));
      p.set("price_max", String(filters.priceRange[1]));
    }
    return p.toString();
  }, [filters]);

  const prevChatProductsLengthRef = useRef(0);
  useEffect(() => {
    const currentLength = chatProducts.length;
    prevChatProductsLengthRef.current = currentLength;

    if (currentLength > 0 && !isManualFilterChange.current) {
      const validProducts = chatProducts.filter((p) => p?.id || p?.sku);
      if (validProducts.length > 0) {
        setProducts(validProducts);
        setTotalProducts(validProducts.length);
        setLoading(false);
        setUsingChatProducts(true);
      }
    } else if (currentLength === 0 && usingChatProducts && !chatNoResults) {
      setUsingChatProducts(false);
    }
  }, [chatProducts, usingChatProducts, chatNoResults]);

  useEffect(() => {
    // Chat ran a search that returned nothing: keep the grid empty so the
    // "no products found" state shows, instead of loading the full catalog.
    if (chatNoResults) {
      setProducts([]);
      setLoading(false);
      return;
    }

    if (usingChatProducts && chatProducts.length > 0 && !isManualFilterChange.current) return;

    if (isManualFilterChange.current) {
      setUsingChatProducts(false);
      setChatProducts([]);
      isManualFilterChange.current = false;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await getProducts(apiQueryString);
        if (!cancelled) {
          const finalProducts = Array.isArray(data) ? data : [];
          setProducts(finalProducts);
        }
      } catch (e) {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiQueryString, usingChatProducts, chatProducts.length, chatNoResults]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stats = await getProductStats(statsQueryString);
        if (!cancelled) {
          const total = Number(stats?.total_products ?? 0);
          setTotalProducts(isNaN(total) ? 0 : total);
        }
      } catch {
        if (!cancelled) {
          setTotalProducts((prev) => {
            if (products.length < PAGE_SIZE) {
              return (page - 1) * PAGE_SIZE + products.length;
            }
            return Math.max(prev, page * PAGE_SIZE + 1);
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [statsQueryString, products.length, page]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const computedTotalProducts = useMemo(() => {
    if (usingChatProducts) return products.length;
    if (totalProducts && totalProducts > 0) return totalProducts;
    return (page - 1) * PAGE_SIZE + products.length;
  }, [usingChatProducts, totalProducts, products, page]);

  const totalPages = Math.max(1, Math.ceil(computedTotalProducts / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(1);
    }
  }, [totalPages]);

  const displayedProducts = useMemo(() => {
    if (usingChatProducts) {
      const start = (page - 1) * PAGE_SIZE;
      return products.slice(start, start + PAGE_SIZE);
    }
    return products;
  }, [usingChatProducts, products, page]);

  return (
    <>
      <TopNavbar onTabChange={setTabIndex} />
      <PageContainer>
        <SidebarContainer>
          <ChatInterface />
        </SidebarContainer>

        <MainContent>
          <ProductCardGrid>
            {loading ? (
              Array.from(new Array(8)).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={250}
                    sx={{ borderRadius: 2 }}
                  />
                  <Skeleton variant="text" width="80%" sx={{ mt: 1 }} />
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="rounded" width="50%" height={36} sx={{ mt: 1.5 }} />
                </Box>
              ))
            ) : displayedProducts.length > 0 ? (
              displayedProducts.map((p, index) => {
                const productKey = p?.id || p?.sku || `product-${index}`;
                return <ProductCard key={productKey} product={p} />;
              })
            ) : (
              <Typography
                variant="body1"
                color="textSecondary"
                align="center"
                sx={{ mt: 4 }}
              >
                {chatNoResults && chatQuery
                  ? `No products found for "${chatQuery}". Try a broader term or a different category.`
                  : "No products found. Try adjusting your filters."}
              </Typography>
            )}
          </ProductCardGrid>

          {totalPages > 1 && (
            <Box mt={2} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
              />
            </Box>
          )}
        </MainContent>
      </PageContainer>

      {/* ProductInfo Modal */}
      {productInfoOpen && selectedProduct && (
        <ProductInfo
          id={selectedProduct.id}
          title={selectedProduct.description || `${selectedProduct.brand || ""} ${selectedProduct.name || ""}`}
          brand={selectedProduct.brand}
          description={selectedProduct.description}
          sku={selectedProduct.sku}
          condition={selectedProduct.condition}
          availability={selectedProduct.is_available ? "In Stock" : "Out of Stock"}
          price={selectedProduct.current_price}
          image={selectedProduct.image_urls && selectedProduct.image_urls.length > 0 ? selectedProduct.image_urls[0] : selectedProduct.image_url || "/ProductHolder.png"}
          weight={selectedProduct.weight}
          dimensions={selectedProduct.dimensions}
          stock_quantity={selectedProduct.stock_quantity}
          location={selectedProduct.location}
          pricing_mode={selectedProduct.pricing_mode}
          vendor={selectedProduct.vendor}
          specifications={selectedProduct.specifications}
          onClose={() => {
            setProductInfoOpen(false);
            setSelectedProduct(null);
          }}
        />
      )}
    </>
  );
}
